import React, { useCallback } from 'react';
import { formatCurrency } from 'utils';
import debounce from 'lodash/debounce';
import { Nullable } from 'global';
import { Controller, useFormContext } from 'react-hook-form';
import { Box, Typography, TextField } from '@mui/material';
// import { LenderProfile } from '@trnsact/trnsact-shared-types';
import { CurrencyInputField, SelectField, DateField } from 'components/form';
import { CreditLineFormValues } from '../model';
import { generateCreditLineLog } from '../lib';
import { creditStatusValues } from '../constants';
import { DateFormats } from 'constants/common';
import {
  CreditLineRequest,
  UpdateCreditLineRequestInput,
  CreateCreditLineRequestInput,
  CreditLineStatusEnum,
} from '@trnsact/trnsact-shared-types';

interface Props {
  userName: string;
  requestedAmount: number;
  approvedAmount: number;
  status: CreditLineStatusEnum;
  lenderProfiles: any[];
  onCreditLineUpdate: (data: UpdateCreditLineRequestInput) => Promise<void>;
  onCreditLineCreate: (data: CreateCreditLineRequestInput) => Promise<void>;
  creditLineRequest?: CreditLineRequest;
}

export const CreditLineForm = ({
  status,
  userName,
  requestedAmount,
  approvedAmount,
  onCreditLineUpdate,
  onCreditLineCreate,
  lenderProfiles,
  creditLineRequest,
}: Props) => {
  const { setValue, control, getValues } = useFormContext<CreditLineFormValues>();

  // --- Line Requested / Requested Line (amount) ---

  const debouncedUpdateAmount = useCallback(
    debounce(async (nextAmountValue: number) => {
      await onCreditLineUpdate({
        creditLineRequestId: creditLineRequest?.creditLineRequestId || '',
        approvedAmount: nextAmountValue,
      });
    }, 500),
    [onCreditLineUpdate]
  );

  const handleChangeAmount = async (nextAmountValue: number) => {
    setValue('approvedAmount', nextAmountValue);
    await debouncedUpdateAmount(nextAmountValue);
  };

  // --- Status ---

  const handleChangeStatus = async (nextStatusValue: CreditLineStatusEnum) => {
    setValue('status', nextStatusValue);

    const logMessage = generateCreditLineLog({
      userName,
      currentStatus: status,
      nextStatus: nextStatusValue,
      prevNotes: getValues('allNotes'),
    });

    setValue('allNotes', logMessage);

    await onCreditLineUpdate({
      creditLineRequestId: creditLineRequest?.creditLineRequestId || '',
      status: nextStatusValue as any,
    });
  };

  const handleChangeLineOwner = async (lenderProfileId: string) => {
    await onCreditLineUpdate({
      creditLineRequestId: creditLineRequest?.creditLineRequestId || '',
      lenderProfileId,
    });
  };

  const debouncedUpdateNotes = useCallback(
    debounce(async (nextNotesValue: string) => {
      await onCreditLineUpdate({
        creditLineRequestId: creditLineRequest?.creditLineRequestId || '',
        notes: nextNotesValue,
      });
    }, 500),
    [onCreditLineUpdate]
  );

  const handleNotesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('notes', event.target.value);
    await debouncedUpdateNotes(event.target.value);
  };

  // --- Layout ---

  return (
    <Box className="lineSection" style={{ width: '50%' }}>
      {/* Line Requested row (display only) */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography component="span" variant="body2">
          Line Requested
        </Typography>
        <Typography component="span" variant="body2">
          {formatCurrency(requestedAmount, true)}
        </Typography>
      </Box>

      {/* Approved Line */}
      <CurrencyInputField
        name="approvedAmount"
        control={control}
        label="Approved Line"
        customOnChange={handleChangeAmount}
        textFieldProps={{ fullWidth: true }}
      />

      {/* Status */}
      <Box mt={2}>
        <SelectField
          label="Status"
          control={control}
          name="status"
          options={creditStatusValues}
          extraAction={async nextValue => {
            await handleChangeStatus(nextValue as CreditLineStatusEnum);
          }}
        />
      </Box>

      {/* Approved By */}
      {/* <Box mt={2}>
        <Controller
          name="approvedBy"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Approved By"
            />
          )}
        />
      </Box>

      {/* Approved On */}
      {/* <Box mt={2}>
        <DateField
          control={control}
          label="Approved On"
          format={DateFormats.Short}
          name="approvedOn"
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Box>
      */}

      {/* Line Owner (Lender) */}
      {/* <Box mt={2}>
        <SelectField
          label="Line Owner"
          control={control}
          name="lenderProfileId" // adjust to match your form model
          options={lenderProfiles.map(lp => ({
            label: lp.lenderName,
            value: lp.id,
          }))}
          extraAction={async nextValue => {
            await handleChangeLineOwner(nextValue as string);
          }}
        />
      </Box> */}

      {/* Expiration Date */}
      <Box mt={2}>
        <DateField
          control={control}
          label="Expiration Date"
          format={DateFormats.Short}
          name="expirationDate"
          slotProps={{ textField: { fullWidth: true } }}
          extraAction={async nextDate => {
            await onCreditLineUpdate({
              creditLineRequestId: creditLineRequest?.creditLineRequestId || '',
              expirationDate: nextDate?.toISOString(),
            });
          }}
        />
      </Box>

      {/* Notes */}
      <Box mt={2}>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <TextField {...field} fullWidth multiline minRows={4} label="Notes" onChange={handleNotesChange} />
          )}
        />
      </Box>
    </Box>
  );
};
